// Frontend Logger for client-side error tracking
// Logs to browser console and sends to backend endpoint (optional)

class Logger {
  constructor() {
    this.logToBackend = process.env.REACT_APP_LOG_TO_BACKEND === 'true';
    this.backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    this.logs = [];
    this.maxLocalLogs = 100;
  }

  // Format log entry
  formatLog(level, message, context = {}) {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...context,
        url: window.location.href,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`
      }
    };
  }

  // Store log locally
  storeLog(log) {
    this.logs.push(log);
    if (this.logs.length > this.maxLocalLogs) {
      this.logs.shift(); // Remove oldest log
    }
    
    // Store in localStorage for persistence
    try {
      const storedLogs = JSON.parse(localStorage.getItem('app_logs') || '[]');
      storedLogs.push(log);
      if (storedLogs.length > this.maxLocalLogs) {
        storedLogs.shift();
      }
      localStorage.setItem('app_logs', JSON.stringify(storedLogs));
    } catch (e) {
      console.error('Failed to store log in localStorage:', e);
    }
  }

  // Send log to backend (optional)
  async sendToBackend(log) {
    if (!this.logToBackend) return;

    try {
      await fetch(`${this.backendUrl}/api/logs/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(log)
      });
    } catch (error) {
      // Don't log this error to avoid infinite loops
      console.error('Failed to send log to backend:', error);
    }
  }

  // Info level logging
  info(message, context = {}) {
    const log = this.formatLog('info', message, context);
    console.info(`[INFO] ${message}`, context);
    this.storeLog(log);
    this.sendToBackend(log);
  }

  // Warning level logging
  warn(message, context = {}) {
    const log = this.formatLog('warn', message, context);
    console.warn(`[WARN] ${message}`, context);
    this.storeLog(log);
    this.sendToBackend(log);
  }

  // Error level logging
  error(message, error = null, context = {}) {
    const errorContext = {
      ...context,
      error: error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : null
    };
    
    const log = this.formatLog('error', message, errorContext);
    console.error(`[ERROR] ${message}`, error, context);
    this.storeLog(log);
    this.sendToBackend(log);
  }

  // Debug level logging (only in development)
  debug(message, context = {}) {
    if (process.env.NODE_ENV !== 'development') return;
    
    const log = this.formatLog('debug', message, context);
    console.debug(`[DEBUG] ${message}`, context);
    this.storeLog(log);
  }

  // Get all stored logs
  getLogs() {
    return this.logs;
  }

  // Get logs from localStorage
  getStoredLogs() {
    try {
      return JSON.parse(localStorage.getItem('app_logs') || '[]');
    } catch (e) {
      console.error('Failed to retrieve logs from localStorage:', e);
      return [];
    }
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
    try {
      localStorage.removeItem('app_logs');
    } catch (e) {
      console.error('Failed to clear logs from localStorage:', e);
    }
  }

  // Download logs as JSON file
  downloadLogs() {
    const logs = this.getStoredLogs();
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `app-logs-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Log API errors
  logApiError(endpoint, error, response = null) {
    this.error('API Request Failed', error, {
      endpoint,
      status: response?.status,
      statusText: response?.statusText,
      data: response?.data
    });
  }

  // Log user actions
  logUserAction(action, details = {}) {
    this.info(`User Action: ${action}`, {
      action,
      ...details,
      userId: localStorage.getItem('userId')
    });
  }
}

// Create singleton instance
const logger = new Logger();

// Global error handler
window.addEventListener('error', (event) => {
  logger.error('Uncaught Error', event.error, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled Promise Rejection', event.reason, {
    promise: event.promise
  });
});

export default logger;
